import jQuery from 'jquery';
import isFiniteNumber from '%app/js/common/services/isFiniteNumber';
import resolveBoundedInteger from '%app/js/common/services/resolveBoundedInteger';
import buildTicketSelectionLine from '%app/js/common/services/ticketSelectionLine';
import {
    MIN_ENTITY_ID,
    MIN_SUBMITTED_TICKETS_COUNT,
} from '%app/js/common/constants/ticketSelectionLimits';

const NO_TICKETS = 0;
const ONE_TICKET = 1;
const MIN_STEPPER_QUANTITY = 1;
const COUNT_PLACEHOLDER = '{N}';
const AMOUNT_FRACTION_DIGITS = 2;

class WidgetManager {
    constructor() {
        this.formElement = '.widget-form';
        this.priceElement = '.price';
        this.countElement = '.event-widget__count';
        this.quantityHintElement = '.quantity-hint';
        this.correctedQuantityHintClass = 'quantity-hint--corrected';
        this.ticketCountElement = '.ticket-count';
        this.priceTypeElement = '.price-type';
        this.priceOptionElement = '.price-option';
        this.selectedPriceOptionClass = 'price-option--selected';
        this.ticketTypeElement = '.ticket-type';
        this.orderDetailsElement = '.order-details';
        this.stepperElement = '.quantity-stepper';
        this.stepperButtonElement = '.quantity-stepper__button';
        this.decrementButtonElement = '[data-step="-1"]';
        this.incrementButtonElement = '[data-step="1"]';
        this.committedQuantityKey = 'committedQuantity';
    }

    resolveTicketsCount($ticketCount) {
        return resolveBoundedInteger($ticketCount.val(), MIN_SUBMITTED_TICKETS_COUNT);
    }

    resolveIdentifier($element) {
        return resolveBoundedInteger($element.data('id'), MIN_ENTITY_ID);
    }

    resolveStepperBounds($stepper) {
        const min = resolveBoundedInteger($stepper.data('min'), MIN_STEPPER_QUANTITY) || MIN_STEPPER_QUANTITY;
        const max = resolveBoundedInteger($stepper.data('max'), NO_TICKETS);

        return { min, max: max === null ? NO_TICKETS : max };
    }

    resolveCommittedQuantity($ticketCount) {
        const committed = $ticketCount.data(this.committedQuantityKey);

        return committed === undefined ? NO_TICKETS : committed;
    }

    clampQuantity(quantity, { min, max }) {
        if (quantity === NO_TICKETS || max < min) {
            return NO_TICKETS;
        }

        return Math.min(Math.max(quantity, min), max);
    }

    stepQuantity($stepper, direction) {
        const bounds = this.resolveStepperBounds($stepper);
        const current = this.resolveCommittedQuantity($stepper.find(this.ticketCountElement));
        let next = current - 1;
        if (direction > 0) {
            next = current === NO_TICKETS ? bounds.min : current + 1;
        } else if (current <= bounds.min) {
            next = NO_TICKETS;
        }

        this.renderQuantity($stepper, this.clampQuantity(next, bounds));
    }

    commitTypedQuantity($ticketCount) {
        const $stepper = $ticketCount.closest(this.stepperElement);
        const typed = this.resolveTicketsCount($ticketCount);
        if (typed === null) {
            this.renderQuantity($stepper, this.resolveCommittedQuantity($ticketCount));
            return;
        }

        const quantity = this.clampQuantity(typed, this.resolveStepperBounds($stepper));
        this.renderQuantity($stepper, quantity, quantity !== typed);
    }

    renderQuantity($stepper, quantity, isCorrected = false) {
        const { max } = this.resolveStepperBounds($stepper);
        const $priceOption = $stepper.closest(this.priceOptionElement);

        $stepper.find(this.ticketCountElement)
            .val(String(quantity))
            .data(this.committedQuantityKey, quantity);
        $stepper.find(this.decrementButtonElement).prop('disabled', quantity === NO_TICKETS);
        $stepper.find(this.incrementButtonElement).prop('disabled', quantity >= max);
        $priceOption.toggleClass(this.selectedPriceOptionClass, quantity > NO_TICKETS);
        $priceOption.find(this.quantityHintElement).toggleClass(this.correctedQuantityHintClass, isCorrected);

        this.updateTotalPrice();
    }

    eachUsableTicketRow(handleRow) {
        jQuery(this.ticketCountElement).each((key, el) => {
            const $ticketCount = jQuery(el);
            const ticketsCount = this.resolveTicketsCount($ticketCount);
            if (ticketsCount === null || ticketsCount === NO_TICKETS) {
                return;
            }

            const $priceOption = $ticketCount.closest(this.priceOptionElement);
            const $priceType = $priceOption.find(this.priceTypeElement);
            const ticketTypeId = this.resolveIdentifier($priceOption.closest(this.ticketTypeElement));
            const priceTypeId = this.resolveIdentifier($priceType);
            if (ticketTypeId === null || priceTypeId === null) {
                return;
            }

            handleRow({
                ticketTypeId,
                priceTypeId,
                ticketsCount,
                price: Number($priceType.data('price')),
            });
        });
    }

    updateTotalPrice() {
        let total = 0;
        let selectedTicketsCount = 0;
        let pricedTicketsCount = 0;
        this.eachUsableTicketRow(({ ticketsCount, price }) => {
            selectedTicketsCount += ticketsCount;
            if (isFiniteNumber(price)) {
                total += price * ticketsCount;
                pricedTicketsCount += ticketsCount;
            }
        });

        this.renderTicketsCount(selectedTicketsCount);
        this.renderTotal(total, pricedTicketsCount);
    }

    renderTicketsCount(ticketsCount) {
        const $count = jQuery(this.countElement);
        const label = $count.attr(ticketsCount === ONE_TICKET ? 'data-label-one' : 'data-label-many');
        if (label !== undefined) {
            $count.text(label.replace(COUNT_PLACEHOLDER, String(ticketsCount)));
        }
    }

    renderTotal(total, pricedTicketsCount) {
        const $priceElement = jQuery(this.priceElement);
        if (pricedTicketsCount === NO_TICKETS) {
            $priceElement.text('');
            return;
        }

        const freeLabel = $priceElement.attr('data-free-label');
        if (total === 0 && freeLabel !== undefined) {
            $priceElement.text(freeLabel);
            return;
        }

        $priceElement.text(this.formatAmount(total, $priceElement.data('currency')));
    }

    resolveLocalization() {
        const $document = jQuery('html');
        const language = $document.attr('lang');
        const country = $document.attr('data-country');
        if (!language) {
            return undefined;
        }

        return country ? `${language}-${country}` : language;
    }

    formatAmount(amount, currency) {
        const roundedAmount = amount.toFixed(AMOUNT_FRACTION_DIGITS);
        try {
            return new Intl.NumberFormat(this.resolveLocalization(), { style: 'currency', currency })
                .format(Number(roundedAmount));
        } catch (error) {
            return `${roundedAmount} ${currency}`;
        }
    }

    submitForm() {
        const orderDetails = [];
        this.eachUsableTicketRow(({ ticketTypeId, priceTypeId, ticketsCount }) => {
            orderDetails.push(buildTicketSelectionLine(ticketTypeId, priceTypeId, ticketsCount));
        });
        jQuery(this.orderDetailsElement).val(JSON.stringify(orderDetails));
    }

    handleQuantityKeydown(event) {
        const $ticketCount = jQuery(event.currentTarget);
        if (event.key === 'Enter') {
            event.preventDefault();
            this.commitTypedQuantity($ticketCount);
            return;
        }

        const direction = { ArrowUp: 1, ArrowDown: -1 }[event.key];
        if (direction !== undefined) {
            event.preventDefault();
            this.commitTypedQuantity($ticketCount);
            this.stepQuantity($ticketCount.closest(this.stepperElement), direction);
        }
    }

    init() {
        jQuery('body')
            .on('click', this.stepperButtonElement, null, (event) => {
                const $button = jQuery(event.currentTarget);
                this.stepQuantity($button.closest(this.stepperElement), Number($button.data('step')));
            })
            .on('focus', this.ticketCountElement, null, (event) => {
                event.currentTarget.select();
            })
            .on('keydown', this.ticketCountElement, null, (event) => {
                this.handleQuantityKeydown(event);
            })
            .on('change', this.ticketCountElement, null, (event) => {
                this.commitTypedQuantity(jQuery(event.currentTarget));
            })
            .on('submit', this.formElement, null, () => {
                this.submitForm();
            });
    }
}

export default WidgetManager;
